import { FeesExplanationCoachIcon } from "@images/svgIcons/illustrations/feesExplanationCoachIcon"
import { FeesExplanationOwnerIcon } from "@images/svgIcons/illustrations/feesExplanationOwnerIcon"
import { useCurrentBox } from "@modules/box/useBox"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUText } from "@modules/ui/components/huText"
import { Equal, Plus } from "lucide-react"
import { useTheme } from "styled-components"

type FinanceFeesLayoutProps = {} & React.HTMLAttributes<HTMLDivElement>

export const FinanceFeesLayout: React.FC<FinanceFeesLayoutProps> = (props) => {
  const defaultClassName = "flex flex-column gap-5 w-full pb-5"
  const theme = useTheme()
  const language = useLanguage()
  const { result: boxId } = useCurrentBox()

  const feesStandardBankCardPercentage = 1.5
  const feesStandardBankCardFixed = 0.25
  const feesProfessionalBankCardPercentage = 1.5
  const feesProfessionalBankCardFixed = 0.25
  const feesEnglandBankCardPercentage = 2.5
  const feesEnglandBankCardFixed = 0.25
  const feesInternationalBankCardPercentage = 3.25
  const feesInternationalBankCardFixed = 0.25
  const feesSepaFixed = 0.35
  const feesStripePercentage = 1.35
  const feesStripeFixed = 0.1
  const feesHustleUpCoachPercentage = 4
  const feesHustleUpOwnerPercentage = 1

  const exampleTransactionAmount = 100
  const exampleFeesBankCardPercentageAmount = (exampleTransactionAmount * feesStandardBankCardPercentage) / 100
  const exampleFeesStripePercentageAmount = (exampleTransactionAmount * feesStripePercentage) / 100
  const exampleFeesHustleUpPercentageAmount =
    (exampleTransactionAmount * (boxId ? feesHustleUpOwnerPercentage : feesHustleUpCoachPercentage)) / 100
  const exampleFeesAmount =
    exampleFeesBankCardPercentageAmount +
    exampleFeesStripePercentageAmount +
    exampleFeesHustleUpPercentageAmount +
    feesStandardBankCardFixed +
    feesStripeFixed
  const exampleReceveidAmount = exampleTransactionAmount - exampleFeesAmount

  return (
    <div className={props.className ? props.className : defaultClassName} {...props}>
      <HUText fontStyle="TL" color={theme.neutralColor900}>
        {word("finances.feesExplanation.layout.title")}
      </HUText>

      {/* REMUNERATION */}
      <section className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.neutralColor900}>
          {word("finances.feesExplanation.layout.subTitle.remuneration")}
        </HUText>
        <div className="flex gap-2 border-left-1 border-neutralColor-300 pl-2">
          <div className="flex flex-column gap-2 ">
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
              {word("finances.feesExplanation.layout.dotOne")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
              {word("finances.feesExplanation.layout.dotTwo")}
            </HUText>
            <HUText fontStyle="TS" color={theme.blackAtheleWhiteOwner}>
              {word("finances.feesExplanation.layout.dotThree")}
            </HUText>
            <HUText fontStyle="BS" color={theme.neutralColor700} className="pl-3">
              {word("finances.feesExplanation.layout.remunerationDelay")}
            </HUText>
          </div>
          <div>{boxId ? <FeesExplanationOwnerIcon color={""} /> : <FeesExplanationCoachIcon color={""} />}</div>
        </div>
      </section>

      {/* FEES TRANSACTIONS */}
      <section className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.neutralColor900}>
          {word("finances.feesExplanation.layout.subTitle.feesTransactions")}
        </HUText>
        <div className="flex flex-column gap-3 border-left-1 border-neutralColor-300 pl-2">
          <HUText fontStyle="BM" color={theme.neutralColor600}>
            {word("finances.feesExplanation.layout.text.feesTransactions")}
          </HUText>
          <ul className="flex flex-column gap-2 pl-3">
            <li>
              <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner}>
                {word("finances.feesExplanation.layout.text.feesStandardBankCard")}{" "}
                <span className="font-bold">
                  {`${feesStandardBankCardPercentage} % +
                  ${formatCurrency(feesStandardBankCardFixed, "EUR", language)}`}
                </span>
              </HUText>
            </li>
            <li>
              <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner}>
                {word("finances.feesExplanation.layout.text.feesProfessionalBankCard")}{" "}
                <span className="font-bold">
                  {`${feesProfessionalBankCardPercentage} % +
                  ${formatCurrency(feesProfessionalBankCardFixed, "EUR", language)}`}
                </span>
              </HUText>
            </li>
            <li>
              <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner}>
                {word("finances.feesExplanation.layout.text.feesEnglandBankCard")}{" "}
                <span className="font-bold">{`${feesEnglandBankCardPercentage} % +
                  ${formatCurrency(feesEnglandBankCardFixed, "EUR", language)}`}</span>
              </HUText>
            </li>
            <li>
              <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner}>
                {word("finances.feesExplanation.layout.text.feesInternationalBankCard")}{" "}
                <span className="font-bold">{`${feesInternationalBankCardPercentage} % +
                  ${formatCurrency(feesInternationalBankCardFixed, "EUR", language)}`}</span>
              </HUText>
            </li>
            {boxId && (
              <li>
                <HUText fontStyle="BM" color={theme.blackAtheleWhiteOwner}>
                  {word("finances.feesExplanation.layout.text.feesSepa")}{" "}
                  <span className="font-bold">{formatCurrency(feesSepaFixed, "EUR", language)}</span>
                </HUText>
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* FEES STRIPE */}
      <section className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.neutralColor900}>
          {word("finances.feesExplanation.layout.subTitle.feesStripe")}
        </HUText>
        <div className="flex flex-column gap-2 border-left-1 border-neutralColor-300 pl-2">
          <HUText fontStyle="BM" color={theme.neutralColor600}>
            {word("finances.feesExplanation.layout.text.feesStripe")}{" "}
            <span className="font-bold">
              {`${feesStripePercentage} % +
                ${formatCurrency(feesStripeFixed, "EUR", language)}
              ${word("finances.feesExplanation.layout.text.perTransaction")}`}
            </span>
          </HUText>
        </div>
      </section>

      {/* FEES HUSTLE UP */}
      <section className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.neutralColor900}>
          {word("finances.feesExplanation.layout.subTitle.feesHustleUp")}
        </HUText>
        <div className="flex flex-column gap-2 border-left-1 border-neutralColor-300 pl-2">
          <HUText fontStyle="BM" color={theme.neutralColor600}>
            {word("finances.feesExplanation.layout.text.feesHustleUp")}{" "}
            <span className="font-bold">{boxId ? feesHustleUpOwnerPercentage : feesHustleUpCoachPercentage} %</span>
          </HUText>
        </div>
      </section>

      {/* EXEMPLE */}
      <section className="flex flex-column gap-3 bg-neutralColor-100 p-5 rounded-card">
        <HUText fontStyle="TS" color={theme.neutralColor900}>
          {word("finances.feesExplanation.layout.subTitle.example")}
        </HUText>
        <HUText fontStyle="BM" color={theme.neutralColor600}>
          {word("finances.feesExplanation.layout.text.feesExample.soldProgram.part1")}{" "}
          <span className="">{formatCurrency(exampleTransactionAmount, "EUR", language)}</span>{" "}
          {word("finances.feesExplanation.layout.text.feesExample.soldProgram.part2")}
        </HUText>
        <HUText fontStyle="BM" color={theme.neutralColor600}>
          {word("finances.feesExplanation.layout.text.feesExample.receivedAmount.part1")}{" "}
          <span className="font-bold">{formatCurrency(exampleReceveidAmount, "EUR", language)}</span>{" "}
          {word("finances.feesExplanation.layout.text.feesExample.receivedAmount.part2")}{" "}
          <span className="font-bold">{formatCurrency(exampleFeesAmount, "EUR", language)}</span>
        </HUText>
        <div className="flex flex-column gap-2">
          <HUText fontStyle="BS" color={theme.neutralColor600}>
            {word("finances.feesExplanation.layout.text.detailFees")}
          </HUText>
          <HUText fontStyle="BS" color={theme.neutralColor600} className="flex gap-1 align-items-center">
            <Plus strokeWidth={1} color={theme.neutralColor100} />
            {word("finances.feesExplanation.layout.text.feesStandardBankCardExample")}
            <span className="font-bold">{`${feesStandardBankCardPercentage} % (${formatCurrency(exampleFeesBankCardPercentageAmount, "EUR", language)}) + ${formatCurrency(feesStandardBankCardFixed, "EUR", language)}`}</span>
          </HUText>
          <HUText fontStyle="BS" color={theme.neutralColor600} className="flex gap-1 align-items-center">
            <Plus strokeWidth={1} />
            {word("finances.feesExplanation.layout.subTitle.feesStripe")}{" "}
            <span className="font-bold">{`${feesStripePercentage} % (${formatCurrency(exampleFeesStripePercentageAmount, "EUR", language)}) + ${formatCurrency(feesStripeFixed, "EUR", language)}`}</span>{" "}
            {word("finances.feesExplanation.layout.text.perTransaction")}
          </HUText>
          <HUText fontStyle="BS" color={theme.neutralColor600} className="flex gap-1 align-items-center">
            <Plus strokeWidth={1} />
            {word("finances.feesExplanation.layout.subTitle.feesHustleUp")}{" "}
            <span className="font-bold">{`${boxId ? feesHustleUpOwnerPercentage : feesHustleUpCoachPercentage} % (${formatCurrency(exampleFeesHustleUpPercentageAmount, "EUR", language)})`}</span>
          </HUText>
          <HUText fontStyle="BS" color={theme.neutralColor600} className="flex gap-1 align-items-center">
            <Equal strokeWidth={1} />
            <span className="font-bold">{formatCurrency(exampleFeesAmount, "EUR", language)}</span>
          </HUText>
        </div>
      </section>
    </div>
  )
}
